/** @jsx jsx */
import {
  Modal,
  ModalActionButton,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  ModalProps,
} from "@eworkit/iris";
import { Form, Formik, FormikHelpers } from "formik";
import { FC, useCallback, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { unstable_useId as useId } from "reakit/Id";
import { jsx } from "theme-ui";

import { Project } from "@/data/projects";
import { addTimeReportProject, TimeReport, TimeReportProject, transformTimeReportProject } from "@/data/time-reports";
import { useToasts } from "@/views/components/Toast/useToasts";

import { useTimeReportEdit } from "../edit/TimeReportEditContext";
import {
  PROJECT_NAME_ALREADY_EXISTS_ERROR_CODE,
  TimeReportProjectFormFields,
  TimeReportProjectFormValues,
  TIME_REPORT_PROJECT_DEFAULT_VALUES,
  TIME_REPORT_PROJECT_VALIDATION_SCHEMA,
} from "./TimeReportProjectFormFields";

interface TimeReportProjectModalProps extends ModalProps {
  timeReport: TimeReport;
  projectToUpdate?: TimeReportProject;
}

export const TimeReportProjectModal: FC<TimeReportProjectModalProps> = ({
  timeReport,
  projectToUpdate,
  ...modalProps
}) => {
  const { t } = useTranslation("session");
  const { addToast } = useToasts();
  const { id: formId } = useId();

  const { updateTimeReport: update } = useTimeReportEdit();

  const modalHeader = projectToUpdate
    ? t("TIME_REPORTS.PROJECTS.EDIT_MODAL_HEADER")
    : t("TIME_REPORTS.PROJECTS.ADD_MODAL_HEADER");
  const saveButtonLabel = projectToUpdate ? t("TIME_REPORTS.PROJECTS.SAVE") : t("TIME_REPORTS.PROJECTS.ADD");

  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (values: TimeReportProjectFormValues, actions: FormikHelpers<TimeReportProjectFormValues>) => {
      try {
        setLoading(true);

        if (projectToUpdate) {
          const newTimeReport = transformTimeReportProject(timeReport, projectToUpdate.project.guid, p => ({
            ...p,
            projectName: values.name!,
            project: { ...p.project, name: values.name! },
          }));
          await update(newTimeReport);
        } else {
          const newTimeReport = addTimeReportProject(timeReport, {
            projectName: values.name!,
            items: [],
            project: { name: values.name! } as Project,
          });
          await update(newTimeReport);
        }

        modalProps.dialogState.hide?.();
      } catch (error) {
        switch (error?.payload?.code) {
          case PROJECT_NAME_ALREADY_EXISTS_ERROR_CODE:
            actions.setFieldError("name", PROJECT_NAME_ALREADY_EXISTS_ERROR_CODE);
            break;

          default:
            addToast({
              text: t("common:ERROR_MESSAGES.GENERAL"),
              type: "danger",
            });
            break;
        }
      } finally {
        setLoading(false);
      }
    },
    [projectToUpdate, modalProps, update, addToast, t, timeReport],
  );

  return (
    <React.Fragment>
      <Modal {...modalProps} aria-label={modalHeader}>
        <ModalContainer>
          <ModalHeader
            headerPlaceholder={<ModalHeading>{modalHeader}</ModalHeading>}
            onCloseClick={modalProps.dialogState.hide}
            closeButtonAriaLabel={t("COMPONENTS.MODAL.CLOSE")}
          />

          <ModalContent>
            <Formik<TimeReportProjectFormValues>
              initialValues={{ ...TIME_REPORT_PROJECT_DEFAULT_VALUES, name: projectToUpdate?.projectName }}
              validationSchema={TIME_REPORT_PROJECT_VALIDATION_SCHEMA}
              onSubmit={handleSubmit}>
              {formik => (
                <Form id={formId}>
                  <TimeReportProjectFormFields formik={formik} />
                </Form>
              )}
            </Formik>
          </ModalContent>

          <ModalFooter>
            <ModalActionButton type="button" variant="ghost" onClick={modalProps.dialogState.hide}>
              {t("TIME_REPORTS.PROJECTS.CANCEL")}
            </ModalActionButton>
            <ModalActionButton
              type="submit"
              form={formId}
              variant="primary"
              loading={loading}
              disabled={loading}
              focusable={!loading}>
              {saveButtonLabel}
            </ModalActionButton>
          </ModalFooter>
        </ModalContainer>
      </Modal>
    </React.Fragment>
  );
};
